import { Button, Kind, Size } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { alert } from 'actions/flash'
import Copy from 'components/Icons/Copy'
import Envelope from 'components/Icons/Envelope'
import InfoIcon from 'components/InfoIcon'
import palette from 'constants/palette'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import { buildTranslate } from 'locales'
import React from 'react'
import { Container } from 'react-bootstrap-five'
import { useDispatch } from 'react-redux'
import { ClockIcon } from '../icons'
import { SUPPORT_EMAIL } from './constants'
import StepTimeline from './StepTimeline'

const t = buildTranslate('promo_codes.olo_config')

const ICON_SIZE = 16
const BANNER_ICON_SIZE = 16
// Icon boxes are pinned to the text's own line box so the glyph centers on the
// first line instead of drifting with an eyeballed padding offset.
const BANNER_LINE_HEIGHT = '21px'
const COPY_SUCCESS_TIMEOUT_SECONDS = 5
const FIELD_LABEL_WIDTH = 72

const OloConfig = () => {
  const [css] = useStyletron()
  const dispatch = useDispatch()
  const merchant = useCurrentMerchant()
  const clientId = merchant?.hashid ?? ''
  const brand = merchant?.name ?? ''
  const hasClientId = clientId.trim().length > 0

  const subject = t('email.subject', { brand })
  const bodyText = [
    t('email.greeting'),
    '',
    t('email.body_intro'),
    '',
    clientId,
    '',
    t('email.body_outro'),
    '',
    t('email.sign_off'),
  ].join('\n')

  const clipboardText = [
    `${t('email.to_label')}: ${t('email.to')}`,
    `${t('email.cc_label')}: ${SUPPORT_EMAIL}`,
    `${t('email.subject_label')}: ${subject}`,
    '',
    bodyText,
  ].join('\n')

  // RFC 6068 wants CRLF line breaks in the mailto body; classic Outlook on
  // Windows collapses bare LF (%0A) and runs the paragraphs together. Clipboard
  // text keeps plain \n.
  const mailtoBody = bodyText.replace(/\n/g, '\r\n')
  const mailtoHref = `mailto:?cc=${encodeURIComponent(
    SUPPORT_EMAIL
  )}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(
    mailtoBody
  )}`

  const handleCopy = async () => {
    if (!hasClientId) return
    // Absent outside secure contexts. Surface it rather than leaving the
    // merchant clicking a button that silently does nothing.
    if (!navigator.clipboard?.writeText) {
      dispatch(alert({ key: 'danger', message: t('email.copy_error') }))
      return
    }
    try {
      await navigator.clipboard.writeText(clipboardText)
      dispatch(
        alert({
          key: 'success',
          message: t('email.copy_success'),
          timeout: COPY_SUCCESS_TIMEOUT_SECONDS,
        })
      )
    } catch {
      dispatch(alert({ key: 'danger', message: t('email.copy_error') }))
    }
  }

  const fields = [
    { label: t('email.to_label'), value: t('email.to') },
    { label: t('email.cc_label'), value: SUPPORT_EMAIL },
    { label: t('email.subject_label'), value: subject },
  ]

  return (
    <Container className="pt-l pb-xxl" data-testid="olo-config">
      <div
        role="note"
        data-testid="olo-config-banner"
        className={`p-m ${css({
          backgroundColor: palette.eggYolk10,
          border: `1px solid ${palette.eggYolk}`,
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
        })}`}
      >
        <span
          className={css({
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            height: BANNER_LINE_HEIGHT,
          })}
        >
          <ClockIcon size={BANNER_ICON_SIZE} />
        </span>
        <Text.BodyText4
          color="grey90"
          tag="p"
          className={`mb-0 ${css({ lineHeight: BANNER_LINE_HEIGHT })}`}
        >
          {t('banner_prefix')}
          <Text.BodyText4 bold color="grey90" tag="span">
            {t('banner_provider')}
          </Text.BodyText4>
          {t('banner_suffix')}
        </Text.BodyText4>
      </div>

      <Text.Header3 tag="h2" color="grey90" className="mt-xl mb-xs">
        {t('title')}
      </Text.Header3>
      <Text.BodyText3 color="grey70" tag="p" className="mb-l">
        {t('body')}
      </Text.BodyText3>

      <StepTimeline />

      <div
        className={`mt-l ${css({
          border: `1px solid ${palette.grey20}`,
          borderRadius: '8px',
          backgroundColor: palette.white,
          overflow: 'hidden',
        })}`}
      >
        <div
          className={`grey-05-bkg p-m ${css({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '12px',
            borderBottom: `1px solid ${palette.grey20}`,
          })}`}
        >
          <Text.SmallCaps2 bold color="grey90" tag="span">
            {t('email.title')}
          </Text.SmallCaps2>
          <div className={css({ display: 'flex', gap: '8px' })}>
            <Button
              size={Size.SMALL}
              disabled={!hasClientId}
              startEnhancer={() => <Envelope />}
              // Not window.open: a mailto: in a new tab leaves an orphan blank
              // tab behind in Chrome and is a popup-blocker target. Assigning
              // hands off to the mail client without navigating the page.
              onClick={() => window.location.assign(mailtoHref)}
            >
              {t('email.open')}
            </Button>
            <Button
              kind={Kind.TERTIARY}
              size={Size.SMALL}
              onClick={handleCopy}
              disabled={!hasClientId}
              startEnhancer={() => <Copy size={ICON_SIZE} />}
              overrides={{
                BaseButton: {
                  // Every interactive state is declared here rather than left
                  // to the kind: TERTIARY's own hover/active fills resolve
                  // against theme keys the merchant theme doesn't define, so
                  // the button flipped to a dark surface on hover.
                  style: {
                    backgroundColor: palette.white,
                    borderColor: palette.grey30,
                    color: palette.grey90,
                    ':hover:enabled': {
                      backgroundColor: palette.belizeHole10,
                      borderColor: palette.belizeHole,
                      color: palette.belizeHole,
                    },
                    ':active:enabled': {
                      backgroundColor: palette.belizeHole10,
                      borderColor: palette.belizeHole,
                      color: palette.belizeHole,
                    },
                    ':focus:enabled': {
                      backgroundColor: palette.belizeHole10,
                      borderColor: palette.belizeHole,
                      color: palette.belizeHole,
                    },
                  },
                },
              }}
            >
              {t('email.copy')}
            </Button>
          </div>
        </div>

        {!hasClientId ? (
          <div className="p-m">
            <Text.BodyText4 color="grey70" tag="p" className="m-0">
              {t('email.missing_client_id')}
            </Text.BodyText4>
          </div>
        ) : (
          <>
            <dl className="m-0">
              {fields.map(field => (
                <div
                  key={field.label}
                  className={`px-m py-s ${css({
                    display: 'flex',
                    gap: '16px',
                    borderBottom: `1px solid ${palette.grey20}`,
                  })}`}
                >
                  <dt className={css({ flex: `0 0 ${FIELD_LABEL_WIDTH}px` })}>
                    <Text.BodyText4 color="grey50" tag="span">
                      {field.label}
                    </Text.BodyText4>
                  </dt>
                  <dd className="m-0">
                    <Text.BodyText4 color="grey90" tag="span">
                      {field.value}
                    </Text.BodyText4>
                  </dd>
                </div>
              ))}
            </dl>

            <div className="p-m">
              <Text.BodyText4 color="grey90" tag="p" className="mb-m">
                {t('email.greeting')}
              </Text.BodyText4>
              <Text.BodyText4 color="grey90" tag="p" className="mb-m">
                {t('email.body_intro')}
              </Text.BodyText4>
              <div
                className={`grey-05-bkg mb-m ${css({
                  border: `1px solid ${palette.grey20}`,
                  borderRadius: '6px',
                  padding: '12px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  flexWrap: 'wrap',
                })}`}
              >
                <Text.SmallCaps3 bold color="grey50" tag="span">
                  {t('email.client_id_label')}
                </Text.SmallCaps3>
                <Text.BodyText4
                  bold
                  color="grey90"
                  tag="span"
                  className={css({ fontFamily: 'monospace' })}
                >
                  {clientId}
                </Text.BodyText4>
              </div>
              <Text.BodyText4 color="grey90" tag="p" className="mb-m">
                {t('email.body_outro')}
              </Text.BodyText4>
              <Text.BodyText4 color="grey90" tag="p" className="mb-0">
                {t('email.sign_off')}
              </Text.BodyText4>
            </div>
          </>
        )}
      </div>

      <div
        data-testid="olo-config-footer"
        className={`mt-l p-m ${css({
          backgroundColor: palette.belizeHole10,
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
        })}`}
      >
        <span
          aria-hidden="true"
          className={css({
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            height: BANNER_LINE_HEIGHT,
          })}
        >
          <InfoIcon color={palette.belizeHole} />
        </span>
        <Text.BodyText4
          color="grey70"
          tag="p"
          className={`mb-0 ${css({ lineHeight: BANNER_LINE_HEIGHT })}`}
        >
          {t('footer_prefix')}
          <Text.BodyText4 bold color="grey90" tag="span">
            {t('footer_email')}
          </Text.BodyText4>
          {t('footer_suffix')}
        </Text.BodyText4>
      </div>
    </Container>
  )
}

export default OloConfig
